import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FiSearch, FiX } from 'react-icons/fi';
import { Icon } from '@iconify/react';
import ProgressBar from './ProgressBar';
import 'flag-icons/css/flag-icons.min.css';

const Container = styled.div`
  display: flex;
  gap: 2rem;
  height: 100%;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const IngredientsSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-width: 0;
`;

const Sidebar = styled.div`
  flex: 0 0 350px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex: none;
  }
`;

const LetterFilterContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const LetterButton = styled(motion.button)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${props => props.active ? '#ffb53d' : '#e2e8f0'};
  background: ${props => props.active ? '#ffb53d' : 'white'};
  color: ${props => props.active ? 'white' : '#334155'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #ffb53d;
    background: ${props => props.active ? '#e29606' : '#fff4e6'};
    transform: scale(1.1);
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: #f8fafc;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ffb53d;
    background: white;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 1.2rem;
`;

const LetterSection = styled(motion.div)`
  margin-bottom: 1.5rem;
`;

const LetterHeader = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #ffb53d;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e2e8f0;
`;

const IngredientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ffb53d;
    border-radius: 4px;
  }
`;

const IngredientCard = styled(motion.button)`
  background: white;
  border: 2px solid ${props => props.selected ? '#ffb53d' : '#e2e8f0'};
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 130px;
  position: relative;
  
  &:hover {
    border-color: #ffb53d;
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(255, 181, 61, 0.2);
  }
  
  ${props => props.selected && `
    background: #fff4e6;
    box-shadow: 0 6px 18px rgba(255, 181, 61, 0.3);
  `}
`;

const IngredientIcon = styled.div`
  font-size: 3rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
`;

const IngredientName = styled.div`
  font-weight: 600;
  color: #334155;
  text-align: center;
  line-height: 1.2;
  font-size: 1.05rem;
`;

const SelectedIngredientsPanel = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  flex: 1;
  overflow-y: auto;
`;

const SelectedTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #334155;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SelectedItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #f8fafc;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  border: 1px solid #e2e8f0;
`;

const SelectedLetter = styled.div`
  background: #ffb53d;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
`;

const SelectedInfo = styled.div`
  flex: 1;
`;

const SelectedName = styled.div`
  font-weight: 600;
  color: #334155;
  font-size: 0.9rem;
`;

const SelectedCountry = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CountryFlag = styled.span`
  width: 16px;
  height: 12px;
  border-radius: 1px;
  overflow: hidden;
  display: inline-block;
  
  &.fi {
    background-size: cover;
    background-position: center;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const BuildButton = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: ${props => props.disabled ? '#e2e8f0' : '#ffb53d'};
  color: ${props => props.disabled ? '#94a3b8' : 'white'};
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: #e29606;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(255, 181, 61, 0.4);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #64748b;
  font-style: italic;
  padding: 2rem;
`;

function IngredientSelector({
  ingredients,
  selectedIngredients,
  onAddIngredient,
  onRemoveIngredient,
  onOpenCountryModal,
  canProceed,
  onBuildChampon,
  progress
}) {
  const [selectedLetter, setSelectedLetter] = useState(null);

  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const filteredIngredients = useMemo(() => {
    const toFilter = selectedLetter 
      ? ingredients.filter(ing => ing.letter === selectedLetter)
      : ingredients;
    
    // Group by letter
    const grouped = {};
    toFilter.forEach(ingredient => {
      if (!grouped[ingredient.letter]) {
        grouped[ingredient.letter] = [];
      }
      grouped[ingredient.letter].push(ingredient);
    });
    
    return grouped;
  }, [ingredients, selectedLetter]);

  const isSelected = (ingredient) => {
    return selectedIngredients.some(selected => selected.name === ingredient.name);
  };

  const getButtonText = () => {
    if (selectedIngredients.length < 3) {
      const remaining = 3 - selectedIngredients.length;
      return `Select ${remaining} more ingredient${remaining > 1 ? 's' : ''}`;
    }
    
    const withoutCountry = selectedIngredients.filter(ing => !ing.country).length;
    if (withoutCountry > 0) {
      return `Select ${withoutCountry} more countr${withoutCountry > 1 ? 'ies' : 'y'}`;
    }
    
    return `🍜 Build Champon (${selectedIngredients.length} ingredients)`;
  };

  return (
    <Container>
      <IngredientsSection>
        <LetterFilterContainer>
          {allLetters.map(letter => (
            <LetterButton
              key={letter}
              active={selectedLetter === letter}
              onClick={() => setSelectedLetter(selectedLetter === letter ? null : letter)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {letter}
            </LetterButton>
          ))}
        </LetterFilterContainer>

        <IngredientsGrid>
          {Object.keys(filteredIngredients).sort().map(letter => (
            <LetterSection
              key={letter}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{ gridColumn: '1 / -1' }}
            >
              <LetterHeader>{letter}</LetterHeader>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                gap: '1rem' 
              }}>
                {filteredIngredients[letter].map((ingredient, index) => (
                  <IngredientCard
                    key={ingredient.name}
                    selected={isSelected(ingredient)}
                    onClick={() => {
                      if (isSelected(ingredient)) {
                        const selectedIndex = selectedIngredients.findIndex(
                          selected => selected.name === ingredient.name
                        );
                        onRemoveIngredient(selectedIndex);
                      } else {
                        onAddIngredient(ingredient);
                      }
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <IngredientIcon>
                      {ingredient.iconType === 'image' ? (
                        <img 
                          src={ingredient.icon} 
                          alt={ingredient.name}
                          style={{
                            width: '3rem',
                            height: '3rem',
                            objectFit: 'contain'
                          }}
                        />
                      ) : (
                        <Icon icon={ingredient.icon} width="3rem" height="3rem" />
                      )}
                    </IngredientIcon>
                    <IngredientName>{ingredient.name}</IngredientName>
                  </IngredientCard>
                ))}
              </div>
            </LetterSection>
          ))}
        </IngredientsGrid>
      </IngredientsSection>

      <Sidebar>
        <SelectedIngredientsPanel
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <SelectedTitle>
            🥢 Your Bowl ({selectedIngredients.length}/3)
          </SelectedTitle>
          
          <ProgressBar progress={progress} />
          
          {selectedIngredients.length === 0 ? (
            <EmptyState>
              No ingredients yet.<br />
              Choose 3 ingredients!
            </EmptyState>
          ) : (
            <AnimatePresence>
              {selectedIngredients.map((ingredient, index) => (
                <SelectedItem
                  key={ingredient.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <SelectedLetter>{ingredient.letter}</SelectedLetter>
                  <SelectedInfo>
                    <SelectedName>{ingredient.name}</SelectedName>
                    <SelectedCountry>
                      {ingredient.country ? (
                        <>
                          <CountryFlag className={`fi fi-${ingredient.country.code}`} />
                          {ingredient.country.name}
                        </>
                      ) : (
                        <span style={{ color: '#ef4444' }}>Select Country</span>
                      )}
                    </SelectedCountry>
                  </SelectedInfo>
                  <ActionButton
                    onClick={() => onOpenCountryModal(index)}
                    title={ingredient.country ? 'Change country' : 'Select country'}
                  >
                    🌍
                  </ActionButton>
                  <ActionButton
                    onClick={() => onRemoveIngredient(index)}
                    title="Remove ingredient"
                  >
                    <FiX color="#ef4444" />
                  </ActionButton>
                </SelectedItem>
              ))}
            </AnimatePresence>
          )}
          
          <BuildButton
            disabled={!canProceed}
            onClick={onBuildChampon}
            whileHover={canProceed ? { scale: 1.02 } : {}}
            whileTap={canProceed ? { scale: 0.98 } : {}}
          >
            {getButtonText()}
          </BuildButton>
        </SelectedIngredientsPanel>
      </Sidebar>
    </Container>
  );
}

export default IngredientSelector;
